import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';

export type MarketListPriceChange = string | number | null;

export interface MarketListMarket {
  market_id: string;
  market_slug: string;
  market_question: string;
  category: string;
  outcome_yes: string;
  outcome_no: string;
  outcome_yes_price: string;
  outcome_no_price: string;
  market_active: number;
  market_closed: number;
  end_date: string | null;
  closed_time: string | null;
  group_item_title: string;
  price_change_24h: MarketListPriceChange;
  recommendation: string;
  market_image: string;
  market_icon: string;
  total_volume_usdc: string;
  last_trade_price: string;
  created_at: string;
  data_updated_at: string;
  condition_id: string;
  clob_tokens_id_yes: string;
  uma_resolution_status: 'resolved' | 'proposed' | 'disputed' | null;
}

export interface MarketListTag {
  tag_id: number;
  label: string;
  slug: string;
  category_id: number | null;
  category_name: string | null;
  category_slug: string | null;
}

/**
 * /api/market/markets 的 list 项。/api/market/entity/markets 复用此结构，
 * 但不返回 tags / recommended_questions，且部分字段可能为 null。
 */
export interface MarketListEvent {
  id: number;
  slug: string;
  event_id: number;
  title: string;
  image: string;
  icon: string;
  category: string | null;
  description: string | null;
  ticker: string | null;
  cover: string | null;
  total_volume: string;
  total_liquidity: string;
  end_date: string | null;
  closed_time: string | null;
  closed: number | null;
  active: number | null;
  volume_24hr: string | null;
  time_category?: string;
  tags?: MarketListTag[];
  recommended_questions?: string[];
  markets: MarketListMarket[];
}

export interface MarketListResponse {
  success: boolean;
  data: {
    list: MarketListEvent[];
    limit: number;
    offset: number;
  };
}

const PAGE_SIZE = 20;

export type MarketListMode = 'pagination' | 'infinite';

export interface UseMarketListOptions {
  mode?: MarketListMode;
  limit?: number;
  initialPage?: number;
  exclude_crypto?: boolean;
  /** 搜索词，透传给 /api/market/markets 的 query 参数；变化时分页重置 */
  query?: string;
}

function buildMarketListKey(
  category: string,
  page: number,
  pageSize: number,
  requestLimit = pageSize,
  excludeCrypto = true,
  query = '',
) {
  const searchParams = new URLSearchParams();
  searchParams.append('limit', requestLimit.toString());
  searchParams.append('offset', ((page - 1) * pageSize).toString());
  searchParams.append('exclude_crypto', String(excludeCrypto));

  if (category !== 'all') {
    searchParams.append('category', category);
  }

  if (query) {
    searchParams.append('query', query);
  }

  return `api:/market/markets?${searchParams.toString()}`;
}

export function useMarketList(
  category: string,
  limitOrOptions: number | UseMarketListOptions = PAGE_SIZE,
) {
  const options = typeof limitOrOptions === 'number' ? { limit: limitOrOptions } : limitOrOptions;
  const mode = options.mode || 'pagination';
  const limit = options.limit || PAGE_SIZE;
  const requestLimit = Math.min(limit + 1, 100);
  const canUseLookahead = requestLimit > limit;
  const initialPage = options.initialPage || 1;
  const excludeCrypto = options.exclude_crypto ?? true;
  const query = options.query?.trim() || '';
  const isInfiniteMode = mode === 'infinite';
  const [page, setPage] = useState(initialPage);
  const [infiniteList, setInfiniteList] = useState<MarketListEvent[]>([]);
  const [infiniteHasMore, setInfiniteHasMore] = useState(true);
  const queryScope = useMemo(
    () => `${category}:${limit}:${mode}:${initialPage}:${excludeCrypto}:${query}`,
    [category, excludeCrypto, initialPage, limit, mode, query],
  );
  const [activeQueryScope, setActiveQueryScope] = useState(queryScope);
  const requestPage = activeQueryScope === queryScope ? page : initialPage;

  useEffect(() => {
    setPage(initialPage);
    setInfiniteList([]);
    setInfiniteHasMore(true);
    setActiveQueryScope(queryScope);
  }, [initialPage, queryScope]);

  const marketListKey = buildMarketListKey(
    category,
    requestPage,
    limit,
    requestLimit,
    excludeCrypto,
    query,
  );
  const { data, error, isLoading, isValidating, mutate } = useSWR<MarketListResponse>(
    marketListKey,
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  );

  useEffect(() => {
    if (!isInfiniteMode) return;

    if (error) {
      setInfiniteHasMore(false);
      return;
    }

    if (!data) return;

    const responseList = data.data?.list || [];
    const currentList = responseList.slice(0, limit);
    setInfiniteHasMore(
      canUseLookahead ? responseList.length > limit : responseList.length >= limit,
    );

    if (requestPage === initialPage) {
      setInfiniteList(currentList);
      return;
    }

    setInfiniteList((previousList) => {
      const existingIds = new Set(previousList.map((item) => item.event_id || item.id));
      const nextList = currentList.filter((item) => !existingIds.has(item.event_id || item.id));

      return [...previousList, ...nextList];
    });
  }, [canUseLookahead, data, error, initialPage, isInfiniteMode, limit, requestPage]);

  const responseList = data?.data?.list || [];
  const paginatedList = responseList.slice(0, limit);
  const list = isInfiniteMode ? infiniteList : paginatedList;
  const isLoadingMore = isInfiniteMode && requestPage > initialPage && (isLoading || isValidating);
  const hasMore = isInfiniteMode
    ? infiniteHasMore
    : Boolean(
        !error &&
          data &&
          (canUseLookahead ? responseList.length > limit : responseList.length >= limit),
      );

  const loadMore = useCallback(() => {
    if (isInfiniteMode) {
      if (hasMore && !isLoadingMore) {
        setPage((currentPage) => currentPage + 1);
      }
      return;
    }

    if (hasMore) {
      setPage((currentPage) => currentPage + 1);
    }
  }, [hasMore, isInfiniteMode, isLoadingMore]);
  const nextPage = useCallback(() => {
    if (hasMore) {
      setPage((currentPage) => currentPage + 1);
    }
  }, [hasMore]);
  const previousPage = useCallback(() => {
    setPage((currentPage) => Math.max(initialPage, currentPage - 1));
  }, [initialPage]);

  return {
    list,
    isLoading: isInfiniteMode
      ? requestPage === initialPage && isLoading && infiniteList.length === 0
      : isLoading,
    isLoadingMore,
    hasMore,
    error,
    page: requestPage,
    pageSize: limit,
    pageCount: isInfiniteMode ? requestPage - initialPage + 1 : undefined,
    setPage,
    nextPage,
    previousPage,
    loadMore,
    refresh: mutate,
  };
}
